import React from 'react';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Visibility, Delete, Image, NoPhotography } from '@mui/icons-material';
import { ReactComponent as PlusIcon } from '../../static/images/svg/ic_round-plus.svg';
import ImageSourcePickerDialog from '../image-source-picker/image-source-picker-dialog.component';
import { useState } from 'react';
import AvatarDialog from './avatar-dialog';
import AlertDialog from '../dialog/alert-dialog.component';
import i18n from '../../i18n';
import { createStyles, makeStyles } from '@mui/styles';
import ButtonComponent from '../button/button.component';
import { getRBPStringId } from '../../page/main/main-page-states';
import {
    IMAGE_UPLOAD_SPECS,
    type ImageUploadSpec,
} from '../../utils/image-upload-spec';

/**
 * 头像 / 通用图片卡片：选图走统一 `ImageSourcePickerDialog`（本地 + 网络），确认后回传 File。
 * @param props
 * @returns
 */
export default function AvatarCard(props: {
    // 名称
    name?: string;
    // 头像
    avatarUrl?: string;
    // 大图
    avatarUrlMax?: string;
    // 更换头像
    onChangeAvatarUrl?: (avatarUrl: string, file: File) => void;
    // 删除头像
    onDelete?: () => void;
    // 是否禁用上传图片
    isDisabledUpload?: boolean;
    // 头像容器尺寸（px），不传时保持组件默认 140
    size?: number;
    /**
     * 画幅比例：`legacy` 为正方形（宽高均 `size`）；`firstScreenGallery` 为首页营销槽位（默认 256×160）。
     * 空态视觉统一：虚线框、灰底、主题色加号 + 「添加图片」文案，仅槽位宽高不同。
     */
    uploadEmptyVariant?: 'legacy' | 'firstScreenGallery';
    /** 与 `firstScreenGallery` 配套：槽位宽度（px），默认 256 */
    slotWidth?: number;
    /** 与 `firstScreenGallery` 配套：槽位高度（px），默认 160 */
    slotHeight?: number;
    /**
     * 图位下方 `name` 文案版式：`default` 为历史居中+省略；`firstScreenSlot` 对齐首页与营销「基础设置」等稿（body2、次要色、左齐、可换行）。
     */
    slotCaptionLayout?: 'default' | 'firstScreenSlot';
    /** 为 true 时仅在图片槽位上显示 loading，不遮挡下方名称/说明文案（如基础设置「餐厅环境图」） */
    busy?: boolean;
    /**
     * 空态上传区主文案（如 QOP 模板槽「上传模版」）；不传时沿用 `first_screen_text_add_image` 的 i18n，避免影响其他页面。
     */
    emptyUploadLabel?: string;
    /**
     * 首页营销 `firstScreenGallery` 空槽：点击走自定义逻辑（如打开选菜弹窗），不唤起选图；与虚线空态视觉一致。
     */
    onGalleryEmptyClick?: () => void;
    /**
     * 裁切比例（宽/高）。默认 `1`；菜单/菜品等宽幅图片入口显式传 `16/10`。
     */
    aspectRatio?: number;
    /** 当前图片入口的裁剪与建议输出规格。 */
    uploadSpec?: ImageUploadSpec;
    /**
     * 网络搜图餐厅码。显式优先；否则 fallback `getRBPStringId()`；皆空则弹窗禁用网络 Tab。
     */
    stringId?: string;
    /** 选择器弹窗标题；默认「编辑图片」 */
    pickerTitle?: string;
}) {
    // 图片加载失败
    const [error, setError] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [preViewOpen, setPreviewOpen] = useState(false);
    const theme = useTheme();
    const { avatarUrl, avatarUrlMax, name, isDisabledUpload, busy: busyProp } = props;
    const busy = Boolean(busyProp);
    /** 图位说明是否按首屏营销基础设置稿式展示 */
    const slotCaptionFirstScreen = props.slotCaptionLayout === 'firstScreenSlot';
    const avatarSize = props.size ?? 140;
    /** 是否使用首页与营销大图位（非正方形）画幅 */
    const isGallery = props.uploadEmptyVariant === 'firstScreenGallery';
    /** 槽位宽度（px） */
    const frameW = isGallery ? (props.slotWidth ?? 256) : avatarSize;
    /** 槽位高度（px） */
    const frameH = isGallery ? (props.slotHeight ?? 160) : avatarSize;
    /** 圆角 8px：与 Figma 1838:52035 槽位一致（基于 theme.shape 加倍或回退 8） */
    const slotRadiusPx =
        typeof theme.shape.borderRadius === 'number' ? Math.max(8, theme.shape.borderRadius * 2) : 8;
    const classes = useStyles();

    /**
     * 解析裁切比例：显式 prop > 画廊槽位宽高比 > 默认 1:1。
     */
    const resolvedUploadSpec =
        props.uploadSpec ??
        (props.aspectRatio === 16 / 10
            ? IMAGE_UPLOAD_SPECS.landscape
            : IMAGE_UPLOAD_SPECS.square);
    const resolvedAspectRatio = (() => {
        if (props.uploadSpec) {
            return props.uploadSpec.aspectRatio;
        }
        if (props.aspectRatio != null && Number.isFinite(props.aspectRatio) && props.aspectRatio > 0) {
            return props.aspectRatio;
        }
        if (isGallery && frameH > 0) {
            return frameW / frameH;
        }
        return 1;
    })();

    /**
     * 网络搜图餐厅码：显式 > 当前店 fallback。
     */
    const resolvedStringId = (() => {
        const explicit = String(props.stringId || '').trim();
        if (explicit) {
            return explicit;
        }
        try {
            return String(getRBPStringId() || '').trim();
        } catch {
            return '';
        }
    })();

    /**
     * 打开统一图片选择器。
     */
    const openPicker = () => {
        if (isDisabledUpload || busy) {
            return;
        }
        setPickerOpen(true);
    };

    /**
     * 选择器确认：回传预览与 File。
     */
    const handlePickerConfirm = (payload: { previewUrl: string; file: File }) => {
        props.onChangeAvatarUrl?.(payload.previewUrl, payload.file);
        setPickerOpen(false);
    };

    // 图片地址解析失败时处理
    const onError = () => {
        setError(true);
    };

    React.useEffect(() => {
        setError(false);
    }, [avatarUrl]);

    return (
        <>
            <Box
                justifyContent="center"
                alignItems={slotCaptionFirstScreen && name ? 'flex-start' : 'center'}
                display="flex"
                flexDirection="column"
            >
                {avatarUrl && !error ? (
                    <Box
                        className={classes.imgBox}
                        style={{ width: frameW, height: frameH, borderRadius: `${slotRadiusPx}px` }}
                    >
                        <div className={classes.avatarUpload}>
                            <img
                                alt=""
                                className={classes.avatar}
                                src={avatarUrl}
                                onLoad={() => {
                                    setSuccess(true);
                                }}
                                onError={onError}
                            />
                        </div>
                        <Box display={success ? 'block' : 'none'}>
                            <Box className={classes.iconBox}>
                                <ButtonComponent
                                    titleText={i18n.t('general_text_preview_picture')}
                                    handleClick={() => {
                                        setPreviewOpen(true);
                                    }}
                                    children={<Visibility />}
                                />
                                {props.onChangeAvatarUrl && !isDisabledUpload && (
                                    <UploadImageIcon onClick={openPicker} />
                                )}
                                {props.onDelete && (
                                    <ButtonComponent
                                        color="secondary"
                                        titleText={i18n.t('general_text_delete')}
                                        handleClick={() => {
                                            setDeleteConfirmOpen(true);
                                        }}
                                        children={<Delete />}
                                    />
                                )}
                            </Box>
                        </Box>
                        {busy ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    zIndex: 25,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'var(--overlay-800-a25)',
                                    borderRadius: `${slotRadiusPx}px`,
                                    pointerEvents: 'auto',
                                }}
                                aria-busy="true"
                                aria-live="polite"
                            >
                                <CircularProgress size={32} color="primary" />
                            </Box>
                        ) : null}
                    </Box>
                ) : isGallery && props.onGalleryEmptyClick ? (
                    <ButtonBase
                        focusRipple
                        type="button"
                        disabled={busy || Boolean(isDisabledUpload)}
                        onClick={() => props.onGalleryEmptyClick?.()}
                        aria-label={props.emptyUploadLabel ?? i18n.t('first_screen_text_add_image')}
                        sx={{
                            display: 'block',
                            p: 0,
                            borderRadius: `${slotRadiusPx}px`,
                            width: frameW,
                            height: frameH,
                        }}
                    >
                        <Box
                            className={classes.avatarBox}
                            sx={{
                                position: 'relative',
                                width: frameW,
                                height: frameH,
                                p: 0,
                                borderRadius: `${slotRadiusPx}px`,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: `${slotRadiusPx}px`,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--fill-neutral-100)',
                                    }}
                                    aria-hidden
                                >
                                    <PlusIcon width={20} height={20} />
                                </Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ color: 'text.secondary', textAlign: 'center', fontWeight: 700 }}
                                >
                                    {props.emptyUploadLabel ?? i18n.t('first_screen_text_add_image')}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </ButtonBase>
                ) : !isDisabledUpload ? (
                    <ButtonBase
                        focusRipple
                        type="button"
                        disabled={busy}
                        onClick={openPicker}
                        aria-label={props.emptyUploadLabel ?? i18n.t('first_screen_text_add_image')}
                        sx={{
                            display: 'block',
                            p: 0,
                            borderRadius: `${slotRadiusPx}px`,
                            width: frameW,
                            height: frameH,
                        }}
                    >
                        <Box
                            className={classes.avatarBox}
                            sx={{
                                position: 'relative',
                                width: frameW,
                                height: frameH,
                                p: 0,
                                borderRadius: `${slotRadiusPx}px`,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    width: '100%',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: `${slotRadiusPx}px`,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--fill-neutral-100)',
                                    }}
                                    aria-hidden
                                >
                                    <PlusIcon width={20} height={20} />
                                </Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ color: 'text.secondary', textAlign: 'center', fontWeight: 700 }}
                                >
                                    {props.emptyUploadLabel ?? i18n.t('first_screen_text_add_image')}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </ButtonBase>
                ) : (
                    <div>
                        <Box
                            className={classes.avatarBox}
                            style={{
                                width: frameW,
                                height: frameH,
                                borderRadius: `${slotRadiusPx}px`,
                                opacity: 0.6,
                            }}
                            sx={{
                                position: 'relative',
                                p: 0,
                                border: '1px dashed var(--stroke-neutral-800-a25)',
                                bgcolor: 'var(--fill-neutral-200)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxSizing: 'border-box',
                            }}
                        >
                            <Box
                                className={classes.avatarUpload}
                                style={{ cursor: 'default' }}
                                sx={{
                                    flexDirection: 'column',
                                    gap: 2,
                                    color: 'text.secondary',
                                }}
                            >
                                <NoPhotography sx={{ fontSize: 32 }} />
                                <Typography variant="subtitle1" sx={{ textAlign: 'center', fontWeight: 700 }}>
                                    {i18n.t('general_text_no_photo')}
                                </Typography>
                            </Box>
                            {busy ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 25,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'var(--overlay-800-a25)',
                                        borderRadius: `${slotRadiusPx}px`,
                                        pointerEvents: 'auto',
                                    }}
                                    aria-busy="true"
                                    aria-live="polite"
                                >
                                    <CircularProgress size={32} color="primary" />
                                </Box>
                            ) : null}
                        </Box>
                    </div>
                )}
                {name ? (
                    slotCaptionFirstScreen ? (
                        <Typography
                            component="div"
                            variant="body2"
                            sx={{
                                width: frameW,
                                mt: 2,
                                color: 'var(--text-secondary)',
                                textAlign: 'left',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            }}
                        >
                            {name}
                        </Typography>
                    ) : (
                        <Typography className={classes.avatarName} style={{ width: frameW }}>
                            {name}
                        </Typography>
                    )
                ) : null}
            </Box>
            {pickerOpen ? (
                <ImageSourcePickerDialog
                    open={true}
                    onClose={() => setPickerOpen(false)}
                    onConfirm={handlePickerConfirm}
                    aspectRatio={resolvedAspectRatio}
                    uploadSpec={resolvedUploadSpec}
                    stringId={resolvedStringId || undefined}
                    title={props.pickerTitle}
                />
            ) : null}
            {preViewOpen && (
                <AvatarDialog
                    open={true}
                    onClose={() => {
                        setPreviewOpen(false);
                    }}
                    data={[
                        {
                            imgSrc: avatarUrlMax || avatarUrl || '',
                            imgName: '',
                        },
                    ]}
                />
            )}
            {props.onDelete && (
                <AlertDialog
                    open={deleteConfirmOpen}
                    variant="delete"
                    title={i18n.t('general_text_delete')}
                    highlightText={name?.trim() ? name : undefined}
                    onCancel={() => setDeleteConfirmOpen(false)}
                    onClose={() => setDeleteConfirmOpen(false)}
                    onConfirm={() => {
                        setDeleteConfirmOpen(false);
                        props.onDelete?.();
                    }}
                />
            )}
        </>
    );
}

const useStyles = makeStyles(() =>
    createStyles({
        fileInput: {
            display: 'none',
        },
        avatarUpload: {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            color: '#637381',
            borderRadius: '4px',
        },
        avatar: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
        },
        avatarBox: {
            width: '140px',
            height: '140px',
            borderRadius: '4px',
        },
        imgBox: {
            width: '140px',
            height: '140px',
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative',
        },
        iconBox: {
            justifyContent: 'space-around',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
            display: 'flex',
            opacity: 0,
            background: '#4191ff26',
            '&>span': {
                marginLeft: '0 !important',
                padding: '0 !important',
            },
            '&:hover': {
                opacity: 1,
            },
        },
        icon: {
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            marginLeft: '6px',
            color: '#4191ff',
            backgroundColor: '#4191ff26',
        },
        btn: {
            color: '#4191ff',
            backgroundColor: '#4191ff26',
            borderRadius: '4px',
            display: 'flex',
            cursor: 'pointer',
            width: '40px',
            height: '40px',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
                color: '#4191ff',
                backgroundColor: '#b8e0fe',
            },
        },
        btnLabel: {
            display: 'flex',
            alignItems: 'center',
            padding: '8px',
            marginLeft: '-8px',
        },
        avatarName: {
            padding: '5px 0',
            width: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'center',
        },
    }),
);

/**
 * 上传/更换图片入口图标：点击打开统一选择器（由父组件传入 onClick）。
 */
export function UploadImageIcon(props: {
    /** 点击回调（打开 ImageSourcePickerDialog） */
    onClick: () => void;
}) {
    return (
        <ButtonComponent
            titleText={i18n.t('general_text_change_image')}
            handleClick={() => {
                props.onClick();
            }}
        >
            <Image />
        </ButtonComponent>
    );
}

/**
 * 独立「更换图片」入口：打开统一选择器并回传裁切后 File。
 */
export function UploadImage(props: {
    // 上传图片
    onChange: (file: File) => void;
    /** 裁切比例，默认 1 */
    aspectRatio?: number;
    /** 当前图片入口的裁剪与建议输出规格。 */
    uploadSpec?: ImageUploadSpec;
    /** 网络搜图餐厅码 */
    stringId?: string;
    /** 弹窗标题 */
    pickerTitle?: string;
}) {
    const [pickerOpen, setPickerOpen] = useState(false);

    /**
     * 解析餐厅码。
     */
    const resolvedStringId = (() => {
        const explicit = String(props.stringId || '').trim();
        if (explicit) {
            return explicit;
        }
        try {
            return String(getRBPStringId() || '').trim();
        } catch {
            return '';
        }
    })();

    return (
        <>
            <UploadImageIcon
                onClick={() => setPickerOpen(true)}
            />
            {pickerOpen ? (
                <ImageSourcePickerDialog
                    open={true}
                    onClose={() => setPickerOpen(false)}
                    onConfirm={(payload) => {
                        props.onChange(payload.file);
                        setPickerOpen(false);
                    }}
                    aspectRatio={props.aspectRatio ?? 1}
                    uploadSpec={props.uploadSpec}
                    stringId={resolvedStringId || undefined}
                    title={props.pickerTitle}
                />
            ) : null}
        </>
    );
}
